import React from 'react';
import './farmIcons.css';
import { icons, FileText } from 'lucide-react';
import type { LenseItem, MockEntity } from '@hofsync/design-system';
import type { LenseConfig, LenseCounts } from '../../services/twinService';

/**
 * Convert a kebab-case icon name (from lenses.yaml) to PascalCase (lucide-react key).
 * e.g. "file-text" → "FileText", "arrow-down-left" → "ArrowDownLeft"
 */
function toPascal(name: string): string {
    return name.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('');
}

/**
 * Resolve a Lucide icon name (kebab-case) to a React element.
 * Falls back to FileText if the icon doesn't exist.
 */
export function resolveIcon(iconName: string): React.ReactNode {
    const Icon = icons[toPascal(iconName) as keyof typeof icons];
    if (!Icon) return <FileText />;
    return <Icon />;
}

/**
 * Composite icon: base icon with a small directional badge in the corner.
 * Used for contract items where a document icon is combined with an arrow.
 */
export function CompositeIcon({ base, badge }: { base: string; badge: string }) {
    return (
        <span className="composite-icon">
            <span className="composite-icon__base">{resolveIcon(base)}</span>
            <span className="composite-icon__badge">{resolveIcon(badge)}</span>
        </span>
    );
}

/**
 * Compute lense badge counts from the demo mock entity pool.
 *
 * In demo mode (`twin.entities` flag off) the entity-list path renders
 * mock data (most types via `getEntitiesByType()`, agreements via
 * `MOCK_CONTRACTS` projected to MockEntity in `FarmPage`) while
 * `useLenseCounts()` would otherwise fetch real tenant counts — so a
 * tenant with 37 live persons saw "Personen 37" over a 7-row mock list
 * (Bug 1 in 07-6 audit). This helper produces a `LenseCounts` derived
 * from the same mock pool the entity list uses, so the badge agrees
 * with what the user actually sees.
 *
 * The caller passes `getMockPool(lense)` so the helper stays
 * agnostic about which mock collection backs which `entity_type` —
 * agreements live in `MOCK_CONTRACTS` (not `getEntitiesByType`), and
 * a future demo type would similarly slot in there.
 *
 * Sub-lense filter semantics mirror the FE-side `filterEntities` used
 * on the live path: each filter key/value pair is matched as
 * ``properties[k] === v`` with string coercion. Children with no
 * filter (e.g. `all-persons`) inherit the parent total.
 */
export function computeMockLenseCounts(
    lenses: LenseConfig[],
    getMockPool: (lense: LenseConfig) => MockEntity[],
    q?: string | null,
): LenseCounts {
    const search = q && q.trim().length > 0 ? q.trim().toLowerCase() : null;
    const matchesSearch = (e: MockEntity): boolean => {
        if (!search) return true;
        // Issue #637 — case-insensitive substring on entity_id +
        // every string-valued property. Mirrors the live-mode
        // ``filterEntities`` and the backend ILIKE so demo and live
        // counts stay consistent with their respective entity feeds.
        const haystackParts: string[] = [e.id, e.name];
        for (const value of Object.values(e.properties)) {
            if (typeof value === 'string') haystackParts.push(value);
            else if (typeof value === 'number' || typeof value === 'boolean') {
                haystackParts.push(String(value));
            }
        }
        return haystackParts.join('\n').toLowerCase().includes(search);
    };
    const counts: LenseCounts = {};
    for (const lense of lenses) {
        if (!lense.entity_type) {
            counts[lense.id] = { total: 0, children: {} };
            continue;
        }
        const rawPool = getMockPool(lense);
        const pool = search ? rawPool.filter(matchesSearch) : rawPool;
        const total = pool.length;
        const children: Record<string, number> = {};
        for (const child of lense.children ?? []) {
            const hasContainment = child.filter && Object.keys(child.filter).length > 0;
            const hasMissing = child.filter_missing && child.filter_missing.length > 0;
            if (!hasContainment && !hasMissing) {
                children[child.id] = total;
                continue;
            }
            children[child.id] = pool.filter((e) => {
                const props = (e.properties as Record<string, unknown>) ?? {};
                if (hasContainment) {
                    const ok = Object.entries(child.filter!).every(
                        ([k, v]) => String(props?.[k] ?? '') === v,
                    );
                    if (!ok) return false;
                }
                if (hasMissing) {
                    // Issue #669: each listed key must be missing or
                    // resolve to the empty string in the mock pool — same
                    // SQL semantic the backend applies via UDB.
                    const allMissing = child.filter_missing!.every(
                        (k) => String(props?.[k] ?? '') === '',
                    );
                    if (!allMissing) return false;
                }
                return true;
            }).length;
        }
        counts[lense.id] = { total, children };
    }
    return counts;
}

/**
 * Convert API LenseConfig[] to design-system LenseItem[] for the lense panel.
 */
export function lensesToLenseItems(lenses: LenseConfig[], counts: LenseCounts): LenseItem[] {
    return lenses
        .filter((l) => (counts[l.id]?.total ?? 0) > 0)
        .map((l) => {
            const entry = counts[l.id];
            const children = (l.children ?? [])
                .map((c) => ({
                    id: c.id,
                    name: c.name,
                    count: entry.children[c.id],
                }))
                .filter((c) => (c.count ?? 0) > 0);
            return {
                id: l.id,
                name: l.name,
                icon: resolveIcon(l.icon),
                count: entry.total,
                children,
            };
        });
}
